import { LegacyPortalSearchService } from "@spwn-portal/grpc";
import useSWR from "swr";
import { usePublicConnectClient } from "utility/connectWeb";
import { getBelongedHosting } from "utility/storage";

import { hasKeyword } from "../../../../functions/hasKeyword";
import { roundTimestamp } from "../../../../functions/roundTimestamp";
import { getCheckedPrefectureCodeList } from "../getCheckedPrefectureCodeList";
import { extractCheckedAvailabilityStatusId } from "./functions/extractCheckedAvailabilityStatusId";
import { extractCheckedEventTypesId } from "./functions/extractCheckedEventTypesId";
import { extractCheckedId } from "./functions/extractCheckedId";
import { isInputAsLeastOne } from "./functions/isInputAsLeastOne";
import { useDebouncedValue } from "./functions/useDebouncedValue";

import type { EventSearchCondition } from "../../../../types/eventSearchCondition";
import type { State } from "../useFormState/types/states";
import type {
  LegacyPortalSearchAvailabilityStatus,
  LegacyPortalSearchEventType,
} from "@spwn-portal/grpc";
import type { MutationFetcher } from "swr/mutation";

type Input = State & {
  keyword: string;
};
type Output = {
  canSearch: boolean;
  resultCount: number;
};
type UseRealtimeResultCount = (input: Input) => Output;

const SWR_KEY = "realtimeResultCount";
type SwrKey = [
  Name: typeof SWR_KEY,

  // 各種検索条件
  Keyword: string,
  StartDate: number,
  EndDate: number,
  Genre: string[],
  EventType: LegacyPortalSearchEventType[],
  AvailabilityStatus: LegacyPortalSearchAvailabilityStatus[],
  PrefectureCodes: string[],
];

export const useRealtimeResultCount: UseRealtimeResultCount = (input) => {
  const {
    availabilityStatus,
    endDate,
    eventType,
    genre,
    keyword,
    prefectures,
    startDate,
  } = input;
  const belongedHosting = getBelongedHosting();
  const client = usePublicConnectClient(LegacyPortalSearchService);
  const checkedGenres = genre.checkboxData.reduce(extractCheckedId, []);
  const checkedEventTypes = eventType.checkboxData.reduce(
    extractCheckedEventTypesId,
    []
  );
  const checkedAvailabilityStatus = availabilityStatus.checkboxData.reduce(
    extractCheckedAvailabilityStatusId,
    []
  );
  const checkedPrefectureCodes = getCheckedPrefectureCodeList(prefectures);
  const swrKey: SwrKey = [
    SWR_KEY,
    keyword,
    startDate?.getTime() ?? 0,
    endDate?.getTime() ?? 0,
    checkedGenres,
    checkedEventTypes,
    checkedAvailabilityStatus,
    checkedPrefectureCodes,
  ];
  type Fetcher = MutationFetcher<number, SwrKey, EventSearchCondition>;
  const debouncedKey = useDebouncedValue(swrKey);
  const fetcher: Fetcher = async (_) => {
    if (!hasKeyword(keyword)) return 0;
    const result = await client.searchEvent({
      availabilityStatus: checkedAvailabilityStatus,
      genre: checkedGenres,
      eventType: checkedEventTypes,
      hostingType: belongedHosting,
      keyword,
      openingDateGte: startDate
        ? BigInt(roundTimestamp(startDate.getTime(), "roundToStart"))
        : undefined,
      openingDateLte: endDate
        ? BigInt(roundTimestamp(endDate.getTime(), "roundToEnd"))
        : undefined,
      pageNumber: 1,
      prefectureCodes: checkedPrefectureCodes,
    });
    return result.totalCount;
  };
  const { data } = useSWR<number>(debouncedKey, fetcher, {
    revalidateOnMount: true,
  });

  return {
    canSearch: isInputAsLeastOne(input) && hasKeyword(input.keyword),
    resultCount: data ?? 0,
  };
};
